import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PerinHealthGrid from '../../components/PerinHealthGrid';
import { setSelectedHeaders } from '../../components/PerinHealthGridSlice';
import managementStyles from '../ManagementPages.module.css';
import { useNavigate } from 'react-router-dom';
import { resetProgramState } from './PatientEnrollmentSlice';
import useAxios from '../../services/AxiosConfig';
import FilterManagementView from '../../components/FilterManagementView';
import PatientEnrollmentPopUp from './PatientEnrollmentPopUp';
import { setListValue } from './EnrollmentListSlicer';
import { mergeModalitiesAndSerialNumbers } from './SavedModalitiesSlicer';
import { resetModalitiesState } from './PatientModalitiesSlice';
import { setSelectedMenuItem } from '../../slicers/sidebarSlice';
import { formatPatientsForGrid, savePatientEnrollmentData } from '../../utils/enrollmentUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styles from './PatientEnrollment.module.css';
import Logger from '../../services/Logger';
import { LogElements, LogAction } from '../../services/LogElements';
import { setPatientInfo, setEnrollmentFormValue, setEnrollmentState, setFormValue } from './PatientEnrollmentSlice';
import { showAlert } from '../../components/PerinHealthAlertSlice';

const PatientEnrollment = () => {

    const dispatch = useDispatch();
    const userDateFormat = useSelector((state) => state.userInfo.dateFormat);
    useEffect(() => {
        dispatch(resetProgramState());
        dispatch(resetModalitiesState());
    }, [dispatch]);


    const navigate = useNavigate();
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const httpService = useAxios(axiosConfig.envURL);
    const userId = useSelector((state) => state.userInfo.id);
    const [showEnrollmentPopup, setShowEnrollmentPopup] = useState({
        showing: false,
        item: null,
        type: null,
    });
    const [activeTab, setActiveTab] = useState('enrolled');

    const headers = useMemo(() => activeTab === 'enrolled' ? [
        { key: 'patientId', label: "ID" },
        { key: 'name', label: "Patient Name" },
        { key: 'programNames', label: "Program Name" },
        { key: 'durations', label: "Program Duration" },
        { key: 'datesEnrolled', label: "Date Enrolled" },
        { key: 'expectedDischargeDates', label: "Expected Discharge Date" },
        { key: 'groupName', label: "Group" }
    ] : [
        { key: 'patientId', label: "ID" },
        { key: 'name', label: "Patient Name" },
        { key: 'programNames', label: "Program Name" },
        { key: 'durations', label: "Program Duration" },
        { key: 'datesEnrolled', label: "Date Enrolled" },
        { key: 'datesDischarged', label: "Date Discharged" },
        { key: 'groupName', label: "Group" }
    ], [activeTab]);


    const onAddPatientHandler = () => {
        navigate(`/enroll-patient`);
    };

    const handleTabSwitch = (tabName) => {
        setActiveTab(tabName);
    };

    const [patientList, setPatientList] = useState([]);
    const [allPatients, setAllPatients] = useState([]);

    const getFilteredPatients = useCallback(() => {
        if (activeTab === 'enrolled') {
            return allPatients.filter(patient => patient.status === 'Enrolled');
        } else {
            return allPatients.filter(patient => patient.status === 'Previously Enrolled');
        }
    }, [activeTab, allPatients]);


    const formatPatients = useCallback((data) => {
        const { formattedEnrollmentsArray, enrollmentsByPatientId } = formatPatientsForGrid(data, userDateFormat);
        dispatch(setListValue({ "key": "previousEnrollments", "value": enrollmentsByPatientId }));
        return formattedEnrollmentsArray;
    }, [dispatch, userDateFormat]);


    const getPatients = useCallback(async () => {
        try {
            const [
                enrollments,
            ] = await Promise.all([
                httpService.get(`enrollment/list/${userId}`),
            ]);
            const formatted = formatPatients(enrollments.data.data);
            setAllPatients(formatted);
        } catch (error) {
            console.log("Error fetching enrollments:", error);
        }
    }, [httpService, userId, formatPatients]);


    useEffect(() => {
        getPatients();
    }, [userDateFormat, getPatients]);

    useEffect(() => {
        dispatch(setSelectedMenuItem('Enrollment'));
    }, [dispatch]);

    useEffect(() => {
        // Set selected headers based on active tab
        if (activeTab === 'enrolled') {
            // For enrolled tab, select all columns except datesDischargedDisplay
            const enrolledHeaders = headers.filter(header => header.key !== 'datesDischarged');
            dispatch(setSelectedHeaders(enrolledHeaders));
        } else {
            // For previously enrolled tab, select all columns including datesDischargedDisplay
            dispatch(setSelectedHeaders(headers));
        }
    }, [activeTab, headers, dispatch]);

    useEffect(() => {
        const filteredPatients = getFilteredPatients();
        setPatientList(filteredPatients);
    }, [getFilteredPatients]);


    const getDropdownOptions = (item) => {
        const options = [];
        const enrolled = item.status === "Enrolled";
        const hasPreviousEnrollments = enrolled && item.previouslyEnrolledSelection.length !== 0;
        const previouslyEnrolled = item.status === "Previously Enrolled";
        if (enrolled) {
            options.push({label:"Add Care Program", type:"adding", icon:<FontAwesomeIcon icon="fa-solid fa-plus" />});
            options.push({label:"Update", type:"update", icon:<FontAwesomeIcon icon="fa-solid fa-arrows-rotate" />});
            options.push({label:"Discharge", type:"remove", icon:<FontAwesomeIcon icon="fa-solid fa-ban" />});
            options.push({label:"View (read-only)", type:"view", icon:<FontAwesomeIcon icon="fa-regular fa-eye" />});
        } else {
            options.push({label:"Enroll in a new program", type:"adding", icon:<FontAwesomeIcon icon="fa-solid fa-plus" />});
            options.push({label:"View (read-only)", type:"view", icon:<FontAwesomeIcon icon="fa-regular fa-eye" />});
        }
        if (hasPreviousEnrollments || previouslyEnrolled) {
            options.push({label:"Re-enroll in previous program", type:"reenroll", icon:<FontAwesomeIcon icon="fa-solid fa-repeat" />});
        }

        return options;
    };

    const fetchPatientEnrollmentData = async (patientId, filterByActive = false) => {
        try {
            const url = filterByActive ? `enrollment/patient/active/${patientId}` : `enrollment/patient/${patientId}`;
            const response = await httpService.get(url);
            return response.data?.data || [];
        } catch (error) {
            dispatch(
                showAlert({
                    header: "Error",
                    message: "There was an error updating this program: " + error.message,
                    type: 'danger'
                })
            );
            console.log("Error fetching patient enrollment data:", error);
            return [];
        }
    };

    const handleAdd = async (item) => {
        const patientEnrollmentData = await fetchPatientEnrollmentData(item.patientId, false);
        if (patientEnrollmentData?.length > 0) {
            const { currentModalities, currentSerialNumbers } = savePatientEnrollmentData(patientEnrollmentData);
            dispatch(mergeModalitiesAndSerialNumbers({ modalities: currentModalities, serialNumbers: currentSerialNumbers }));
        }
        dispatch(setPatientInfo(item.data[0].patient));
        dispatch(setFormValue({key:"adding", value: true}));
        dispatch(setFormValue({key:"organizationId", value: item.organizationId}));
        dispatch(setEnrollmentFormValue({key:"groups", value: item.data[0].enrollment.groups}));
        navigate('/enroll-patient');
    };

    const handleUpdate = async (item) => {
        const patientEnrollmentData = await fetchPatientEnrollmentData(item.patientId, true);
        if (patientEnrollmentData?.length === 0) {
            dispatch(
                showAlert({
                    header: "Error",
                    message: "There was an error updating this program",
                    type: 'danger'
                })
            );
            return;
        }
        const { currentModalities, currentSerialNumbers } = savePatientEnrollmentData(patientEnrollmentData);
        dispatch(mergeModalitiesAndSerialNumbers({ modalities: currentModalities, serialNumbers: currentSerialNumbers }));
        const updatingInfo = patientEnrollmentData[0];
        dispatch(setEnrollmentState(updatingInfo));
        dispatch(setFormValue({key:"updatingProgramOptions", value:patientEnrollmentData}));
        navigate('/enroll-patient');
    };

    const handleViewProgram = async (item) => {
        const patientEnrollmentData = await fetchPatientEnrollmentData(item.patientId, false);
        if (patientEnrollmentData?.length === 0) {
            dispatch(
                showAlert({
                    header: "Error",
                    message: "There was an error viewing this patient's enrollments",
                    type: 'danger'
                })
            );
            return;
        }
        const { currentModalities, currentSerialNumbers } = savePatientEnrollmentData(patientEnrollmentData);
        dispatch(mergeModalitiesAndSerialNumbers({ modalities: currentModalities, serialNumbers: currentSerialNumbers }));
        const previousEnrollments = patientEnrollmentData.filter((e) => e.enrollment.dischargeDate);
        const currentEnrollments = patientEnrollmentData.filter((e) => !e.enrollment.dischargeDate);
        let viewingInfo = null;
        if (item.status === "Enrolled") {
            viewingInfo = currentEnrollments[0];
        } else {
            viewingInfo = previousEnrollments[0];
        }
        const groups = viewingInfo.enrollment.groups;
        const organizationId = viewingInfo.hasOwnProperty("organizationId") ? viewingInfo.organizationId : false;
        const newGroups = groups.map((group)=> {
            return organizationId ? {...group,organizationId:organizationId} : group
        });
        dispatch(setEnrollmentFormValue({key:"groups", value:newGroups}));
        dispatch(setEnrollmentState(viewingInfo));
        dispatch(setFormValue({key:"viewing", value:true}));
        dispatch(setFormValue({key:"updating", value:false}));
        dispatch(setFormValue({key:"availableEnrollments", value:patientEnrollmentData}));
        navigate('/view-program');
    };

    const onRowClickHandler = async ({type,item}) => {

        if (type === 'adding') {
            handleAdd(item);
        } else if (type === "update") {
            handleUpdate(item);
        } else if (type === "view") {
            handleViewProgram(item);
        } else {
            let actionName;
            
            switch(type) {
                case 'remove':
                    actionName = 'Discharge';
                    break;
                case 'reenroll':
                    actionName = 'Re-enroll in previous program';
                    break;
                default:
                    actionName = type;
            }
            const patientName = item.name || `${item.firstName || ''} ${item.lastName || ''}`.trim() || 'Unknown Patient';
            const customMessage = `${actionName} - ${patientName}`;
            Logger.shared.info(
                LogElements.gridEllipsesButton,
                LogAction.clicked,
                'PatientEnrollmentGridOptionCard.js',
                customMessage
            );
            setShowEnrollmentPopup({
                showing: true,
                item: item,
                type: type
            })
        }
    };

    const closePopup = (updateGrid = false) => {
        if (updateGrid) {
            getPatients();
        }
        setShowEnrollmentPopup({ showing: false });
    };

    return (
        <div className={managementStyles.MiddleContent}>
            <div className={styles.tabContainer}>
                <div className={styles.tabContent}>
                    <div className={styles.tabWrapper}>
                        <div
                            className={`${styles.tab} ${activeTab === 'enrolled' ? styles.tabActive : styles.tabInactive}`}
                            onClick={() => handleTabSwitch('enrolled')}
                        >
                            <span className={`${styles.tabText} ${activeTab === 'enrolled' ? styles.tabTextActive : styles.tabTextInactive}`}>
                                Enrolled
                            </span>
                        </div>
                        <div
                            className={`${styles.tab} ${activeTab === 'previouslyEnrolled' ? styles.tabActive : styles.tabInactive}`}
                            onClick={() => handleTabSwitch('previouslyEnrolled')}
                        >
                            <span className={`${styles.tabText} ${activeTab === 'previouslyEnrolled' ? styles.tabTextActive : styles.tabTextInactive}`}>
                                Previously Enrolled
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <FilterManagementView
                isOrganization={true}
                isGroup={true}
                isSubGroup={true}
                isTeam={true}
                addButtonLabel='Enroll Patient'
                onAddHandler={onAddPatientHandler}
                showDeleteButton={false}
            />
            <div className={managementStyles.GridContainer}>
                <PerinHealthGrid
                    dataList={patientList}
                    headers={headers}
                    showCheckbox={false}
                    showEditIcon={true}
                    accounts={true}
                    onRowClick={onRowClickHandler}
                    moreEditOptions={getDropdownOptions}
                    showHeaderFilter={true}
                    showProfilePicture={'name'}
                    gridHeight="100%"
                />
            </div>
            {showEnrollmentPopup.showing &&
               <PatientEnrollmentPopUp type={showEnrollmentPopup.type} item={showEnrollmentPopup.item} onClose={closePopup} fetchPatientEnrollmentData={fetchPatientEnrollmentData}/>
            }
        </div>

    )
}

export default PatientEnrollment;
